import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDepartment } from '../context/DepartmentContext'
import { useToast } from '../context/ToastContext'
import { FileText, Upload, FileSpreadsheet, Layers, CheckCircle, CalendarDays, Trash2, Loader, PlusCircle, Download } from 'lucide-react'
import ManualEntry from '../components/ingestion/ManualEntry'
import PdfUpload from '../components/ingestion/PdfUpload'
import ExcelUpload from '../components/ingestion/ExcelUpload'
import BakeryEntry from '../components/ingestion/BakeryEntry'
import SingleDayUpload from '../components/ingestion/SingleDayUpload'
import AddToLog from '../components/ingestion/AddToLog'
import DailyOps from './DailyOps'
import DateInput from '../components/ui/DateInput'
import DateCalendar from '../components/staff/DataEntry/DateCalendar'
import {
  getMorningLogs, getSurplus, getSalesTransactions,
  getProductionLogs, getDistributionLogs,
  getExpenditure, getCashOnHand,
  deleteMorningLogBatch, deleteSurplusBatch, deleteSalesForDate,
  deleteProductionForDate, deleteDistributionForDate,
  getVorsWithSteak, setVorsWithSteak, fetchVorsWithSteak,
} from '../lib/store'

const MODES = [
  { id: 'manual', label: 'Manual Entry', icon: FileText, desc: 'Type data in by hand' },
  { id: 'add-to-log', label: 'Add to Log', icon: PlusCircle, desc: 'Quick-add to today\'s log' },
  { id: 'single-upload', label: 'Single-Day Upload', icon: Upload, desc: 'Upload a document for one day' },
  { id: 'bulk', label: 'Multi-Day Import', icon: Layers, desc: 'Upload multi-day files (Excel/PDF)' },
]

const KITCHEN_DATA_TYPES = [
  { id: 'morning', label: 'Morning Log' },
  { id: 'sales', label: 'Sales' },
  { id: 'surplus', label: 'Surplus' },
]

const BAKERY_DATA_TYPES = [
  { id: 'production', label: 'Production Log' },
  { id: 'distribution', label: 'Distribution' },
  { id: 'summary', label: 'Summary' },
]

const YAWEHS_DATA_TYPES = [
  { id: 'production', label: 'Production Log' },
  { id: 'distribution', label: 'Distribution' },
  { id: 'summary', label: 'Summary' },
]

const TUCKSHOP_DATA_TYPES = [
  { id: 'sales', label: 'Sales' },
  { id: 'surplus', label: 'Surplus / Stock Count' },
]

function getDataTypes(dept) {
  if (dept === 'yawehs') return YAWEHS_DATA_TYPES
  if (dept === 'bakery') return BAKERY_DATA_TYPES
  if (dept === 'tuckshop') return TUCKSHOP_DATA_TYPES
  return KITCHEN_DATA_TYPES
}

// Check if a day has data for a given data type
function dayHasData(department, date, dataType) {
  const isBakery = department === 'bakery' || department === 'yawehs'
  const isTuckshop = department === 'tuckshop'
  try {
    if (isTuckshop) {
      if (dataType === 'sales') return getSalesTransactions(department, date).length > 0
      if (dataType === 'surplus') return getSurplus(department, date).length > 0
    } else if (!isBakery) {
      if (dataType === 'morning') return getMorningLogs(department, date).length > 0
      if (dataType === 'sales') return getSalesTransactions(department, date).length > 0
      if (dataType === 'surplus') return getSurplus(department, date).length > 0
    } else {
      if (dataType === 'production') return getProductionLogs(date, null, department).length > 0
      if (dataType === 'distribution') return getDistributionLogs(date, null, department).length > 0
      if (dataType === 'summary') return getProductionLogs(date, null, department).length > 0 || getDistributionLogs(date, null, department).length > 0
      if (dataType === 'expenditure') return getExpenditure(date, department).length > 0
      if (dataType === 'cash') return getCashOnHand(date, department).length > 0
      if (dataType === 'sandwiches') return getSalesTransactions(department, date).length > 0
    }
  } catch { /* ignore */ }
  return false
}

// Generate an array of dates between start and end (inclusive)
function getDatesBetween(start, end) {
  const dates = []
  const d = new Date(start)
  const e = new Date(end)
  while (d <= e) {
    dates.push(d.toISOString().split('T')[0])
    d.setDate(d.getDate() + 1)
  }
  return dates
}

function formatDisplayDate(iso) {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

function dayLabel(iso) {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })
}

function getDatesInRange(start, end) {
  const dates = []
  const d = new Date(start)
  const e = new Date(end)
  while (d <= e) {
    dates.push(d.toISOString().split('T')[0])
    d.setDate(d.getDate() + 1)
  }
  return dates
}

async function exportRangeToExcel(department, startDate, endDate) {
  const XLSX = (await import('xlsx')).default || await import('xlsx')
  const wb = XLSX.utils.book_new()
  const isBakery = department === 'bakery' || department === 'yawehs'
  const isTuckshop = department === 'tuckshop'
  const dates = getDatesInRange(startDate, endDate)

  if (!isBakery && !isTuckshop) {
    // Kitchen: Morning Log, Sales, Surplus — one row per date per category
    const morningRows = []
    const salesRows = []
    const surplusRows = []
    for (const date of dates) {
      const morning = getMorningLogs(department, date)
      const sales = getSalesTransactions(department, date)
      const surplus = getSurplus(department, date)
      if (morning.length > 0) {
        morning.forEach(r => morningRows.push({ Date: date, Category: r.category, Value: r.value, Unit: r.unit || '' }))
      } else {
        morningRows.push({ Date: date, Category: '— No data recorded —', Value: '', Unit: '' })
      }
      if (sales.length > 0) {
        sales.forEach(r => salesRows.push({
          Date: date, 'Product Code': r.productCode, Product: r.productName,
          Category: r.category, 'Unit Price': r.unitPrice, 'Qty Sold': r.qtySold, 'Sales (R)': r.salesRands,
        }))
      } else {
        salesRows.push({ Date: date, 'Product Code': '— No data recorded —', Product: '', Category: '', 'Unit Price': '', 'Qty Sold': '', 'Sales (R)': '' })
      }
      if (surplus.length > 0) {
        surplus.forEach(r => surplusRows.push({ Date: date, Category: r.category, Value: r.value, Unit: r.unit || '' }))
      } else {
        surplusRows.push({ Date: date, Category: '— No data recorded —', Value: '', Unit: '' })
      }
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(morningRows), 'Morning Log')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(salesRows), 'Sales')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(surplusRows), 'Surplus')
  } else if (isTuckshop) {
    const salesRows = []
    const surplusRows = []
    for (const date of dates) {
      const sales = getSalesTransactions(department, date)
      const surplus = getSurplus(department, date)
      if (sales.length > 0) {
        sales.forEach(r => salesRows.push({
          Date: date, 'Product Code': r.productCode, Product: r.productName,
          Category: r.category, 'Unit Price': r.unitPrice, 'Qty Sold': r.qtySold, 'Sales (R)': r.salesRands,
        }))
      } else {
        salesRows.push({ Date: date, 'Product Code': '— No data recorded —', Product: '', Category: '', 'Unit Price': '', 'Qty Sold': '', 'Sales (R)': '' })
      }
      if (surplus.length > 0) {
        surplus.forEach(r => surplusRows.push({ Date: date, Category: r.category, Value: r.value, Unit: r.unit || '' }))
      } else {
        surplusRows.push({ Date: date, Category: '— No data recorded —', Value: '', Unit: '' })
      }
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(salesRows), 'Sales')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(surplusRows), 'Surplus')
  } else {
    // Bakery/Yawehs
    const prodRows = []
    const distRows = []
    const expRows = []
    const cashRows = []
    for (const date of dates) {
      const production = getProductionLogs(date, null, department)
      const distribution = getDistributionLogs(date, null, department)
      const expenditure = getExpenditure(date, department)
      const cash = getCashOnHand(date, department)
      if (production.length > 0) {
        production.forEach(r => prodRows.push({ Date: date, 'Product Code': r.productCode, Shift: r.shift, 'Qty Produced': r.qtyProduced }))
      } else {
        prodRows.push({ Date: date, 'Product Code': '— No data recorded —', Shift: '', 'Qty Produced': '' })
      }
      if (distribution.length > 0) {
        distribution.forEach(r => distRows.push({
          Date: date, 'Product Code': r.productCode, Point: r.pointId, Sent: r.qtySent,
          'Reported Sales': r.qtySold, 'Reported Surplus': r.reportedSurplus ?? '', Damages: r.damages ?? '',
        }))
      } else {
        distRows.push({ Date: date, 'Product Code': '— No data recorded —', Point: '', Sent: '', 'Reported Sales': '', 'Reported Surplus': '', Damages: '' })
      }
      if (expenditure.length > 0) {
        expenditure.forEach(r => expRows.push({ Date: date, Description: r.description, Amount: r.amount }))
      } else {
        expRows.push({ Date: date, Description: '— No data recorded —', Amount: '' })
      }
      if (cash.length > 0) {
        cash.forEach(r => cashRows.push({ Date: date, Description: r.description || 'Cash on Hand', Amount: r.amount }))
      } else {
        cashRows.push({ Date: date, Description: '— No data recorded —', Amount: '' })
      }
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(prodRows), 'Production')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(distRows), 'Distribution')
    if (expRows.length > 0) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(expRows), 'Expenditure')
    if (cashRows.length > 0) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(cashRows), 'Cash on Hand')
  }

  XLSX.writeFile(wb, `${department}-${startDate}-to-${endDate}.xlsx`)
  return true
}

async function exportDayToExcel(department, date) {
  const XLSX = (await import('xlsx')).default || await import('xlsx')
  const wb = XLSX.utils.book_new()
  const isBakery = department === 'bakery' || department === 'yawehs'
  const isTuckshop = department === 'tuckshop'
  let hasData = false

  if (!isBakery && !isTuckshop) {
    // Kitchen: Morning Log, Sales, Surplus
    const morning = getMorningLogs(department, date)
    if (morning.length > 0) {
      const rows = morning.map(r => ({ Category: r.category, Value: r.value, Unit: r.unit || '' }))
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Morning Log')
      hasData = true
    }
    const sales = getSalesTransactions(department, date)
    if (sales.length > 0) {
      const rows = sales.map(r => ({
        'Product Code': r.productCode, Product: r.productName, Category: r.category,
        'Unit Price': r.unitPrice, 'Qty Sold': r.qtySold, 'Sales (R)': r.salesRands,
      }))
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Sales')
      hasData = true
    }
    const surplus = getSurplus(department, date)
    if (surplus.length > 0) {
      const rows = surplus.map(r => ({ Category: r.category, Value: r.value, Unit: r.unit || '' }))
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Surplus')
      hasData = true
    }
  } else if (isTuckshop) {
    const sales = getSalesTransactions(department, date)
    if (sales.length > 0) {
      const rows = sales.map(r => ({
        'Product Code': r.productCode, Product: r.productName, Category: r.category,
        'Unit Price': r.unitPrice, 'Qty Sold': r.qtySold, 'Sales (R)': r.salesRands,
      }))
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Sales')
      hasData = true
    }
    const surplus = getSurplus(department, date)
    if (surplus.length > 0) {
      const rows = surplus.map(r => ({ Category: r.category, Value: r.value, Unit: r.unit || '' }))
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Surplus')
      hasData = true
    }
  } else {
    // Bakery/Yawehs: Full Excel matching Desres_Bakery_Daily_Operations.xlsx
    const { BAKERY_PRODUCTS_SEED, DISTRIBUTION_POINTS_SEED } = await import('../data/bakeryData')
    const bakeryProducts = getProducts(department).length > 0 ? getProducts(department) : BAKERY_PRODUCTS_SEED
    const distPoints = DISTRIBUTION_POINTS_SEED

    // Sheet 1: Products
    const prodRows = bakeryProducts.map(p => ({
      Code: p.code, 'Product Name': p.name, Category: p.category,
      'Selling Price (R)': p.unitPrice, 'Active?': 'YES',
    }))
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(prodRows), 'Products')

    // Sheet 2: Distribution Sites
    const siteRows = distPoints.map(s => ({
      Code: s.id.toUpperCase(), 'Site Name': s.name,
      Type: s.paymentType === 'invoice' ? 'Invoice' : 'Cash daily', 'Active?': 'YES',
    }))
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(siteRows), 'Distribution Sites')

    // Sheet 3: Production Log
    const production = getProductionLogs(date, null, department)
    const prodLogRows = production.length > 0
      ? production.map(r => {
          const prod = bakeryProducts.find(p => p.code === r.productCode)
          return {
            'Date (dd/mm/yyyy)': date, 'Shift (Day/Night)': r.shift === 'day' ? 'Day' : 'Night',
            Product: prod?.name || r.productCode, 'Category (auto)': prod?.category || '',
            'Price (R) (auto)': prod?.unitPrice || '', 'Qty Produced': r.qtyProduced, Notes: r.notes || '',
          }
        })
      : [{ 'Date (dd/mm/yyyy)': date, 'Shift (Day/Night)': '', Product: '', 'Category (auto)': '', 'Price (R) (auto)': '', 'Qty Produced': '', Notes: '' }]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(prodLogRows), 'Production Log')
    hasData = production.length > 0

    // Sheet 4: Distribution Log
    const distribution = getDistributionLogs(date, null, department)
    const distRows = distribution.length > 0
      ? distribution.map(r => {
          const prod = bakeryProducts.find(p => p.code === r.productCode)
          const site = distPoints.find(s => s.id === r.pointId)
          const sent = Number(r.qtySent) || 0
          const price = prod?.unitPrice || 0
          const surplus = Number(r.reportedSurplus) || 0
          return {
            'Date (dd/mm/yyyy)': date, 'Site': site?.name || r.pointId,
            Product: prod?.name || r.productCode, 'Category (auto)': prod?.category || '',
            'Price (R)(auto)': price, 'Qty Distributed': sent,
            'Projected (R)': sent * price, 'Surplus Returned (qty)': surplus || '',
            'Qty Sold': Math.max(0, sent - surplus), 'Surplus Value (R)': surplus > 0 ? surplus * price : '',
            Notes: '',
          }
        })
      : [{ 'Date (dd/mm/yyyy)': date, Site: '', Product: '', 'Category (auto)': '', 'Price (R)(auto)': '', 'Qty Distributed': '', 'Projected (R)': '', 'Surplus Returned (qty)': '', 'Qty Sold': '', 'Surplus Value (R)': '', Notes: '' }]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(distRows), 'Distribution Log')
    if (distribution.length > 0) hasData = true

    // Sheet 5: Daily Collections
    const { getDailyCollections } = await import('../lib/store')
    const collections = getDailyCollections(department, date)
    const collRows = collections.length > 0
      ? collections.map(c => {
          const diff = (Number(c.projected) || 0) - (Number(c.collected) || 0)
          return {
            'Date (dd/mm/yyyy)': date, Site: c.siteName || c.siteCode,
            'Projected (R)': c.projected || 0, 'Money Collected (R)': c.collected || 0,
            'Difference (R)': diff,
            Status: diff > 0 ? `SHORT R${Math.round(diff)}` : diff < 0 ? `SURPLUS R${Math.abs(Math.round(diff))}` : 'RECONCILED',
            Notes: c.notes || '',
          }
        })
      : [{ 'Date (dd/mm/yyyy)': date, Site: '', 'Projected (R)': '', 'Money Collected (R)': '', 'Difference (R)': '', Status: '', Notes: '' }]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(collRows), 'Daily Collections')
    if (collections.length > 0) hasData = true

    // Sheet 6: Damages Log
    const { getDamages } = await import('../lib/store')
    const damages = getDamages(date, department)
    const dmgRows = damages.length > 0
      ? damages.map(d => {
          const prod = bakeryProducts.find(p => p.code === d.productCode)
          return {
            'Date (dd/mm/yyyy)': date, Product: prod?.name || d.productCode,
            'Category (auto)': prod?.category || '', 'Qty Damaged': d.qty,
            'Loss Value (R)': (d.qty || 0) * (prod?.unitPrice || 0),
            'Notes / Cause': d.notes || '',
          }
        })
      : [{ 'Date (dd/mm/yyyy)': date, Product: '', 'Category (auto)': '', 'Qty Damaged': '', 'Loss Value (R)': '', 'Notes / Cause': '' }]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dmgRows), 'Damages Log')
    if (damages.length > 0) hasData = true
  }

  if (!hasData) return false
  XLSX.writeFile(wb, `${department}-${date}.xlsx`)
  return true
}

// Per-section entry component — manages its own manual/upload mode
function EntrySection({ label, dataType, date, onSaved, isBakery, steakMode }) {
  const [sectionMode, setSectionMode] = useState('manual')
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 flex flex-col">
      {/* Section header with per-section mode buttons */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-brand-400 uppercase tracking-wide flex items-center gap-2">
          <span className="w-1 h-4 bg-brand-500 rounded-sm inline-block" />
          {label}
        </h3>
        <div className="flex gap-1">
          <button
            onClick={() => setSectionMode('manual')}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
              sectionMode === 'manual'
                ? 'bg-brand-700 text-white'
                : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'
            }`}
          >
            <FileText size={11} /> Manual
          </button>
          <button
            onClick={() => setSectionMode('upload')}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
              sectionMode === 'upload'
                ? 'bg-brand-700 text-white'
                : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'
            }`}
          >
            <Upload size={11} /> Upload
          </button>
        </div>
      </div>

      {/* Section content */}
      {sectionMode === 'manual' && !isBakery && (
        <ManualEntry dataType={dataType} date={date} onSaved={onSaved} steakMode={steakMode} />
      )}
      {sectionMode === 'manual' && isBakery && (
        <BakeryEntry dataType={dataType} date={date} onSaved={onSaved} />
      )}
      {sectionMode === 'upload' && (
        <SingleDayUpload dataType={dataType} date={date} onSaved={onSaved} />
      )}
    </div>
  )
}

export default function Ingestion() {
  const { currentDepartmentId } = useDepartment()
  const { addToast } = useToast()
  const [searchParams] = useSearchParams()
  const [mode, setMode] = useState('manual')
  const [bulkFormat, setBulkFormat] = useState('excel')
  const [completionKey, setCompletionKey] = useState(0) // bump to re-check completion

  // Current date for single-day modes (ISO string YYYY-MM-DD)
  const today = new Date().toISOString().split('T')[0]
  const [currentDate, setCurrentDate] = useState(today)

  // Calendar month state — tracks which month the inline calendar is showing
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })

  // Multi-day date range
  const [rangeStart, setRangeStart] = useState('')
  const [rangeEnd, setRangeEnd] = useState('')
  const [selectedDays, setSelectedDays] = useState([])
  const [rangeConfirmed, setRangeConfirmed] = useState(false)

  // Toggle between uploaded vs manual data
  const [viewMode, setViewMode] = useState('manual') // 'manual' | 'uploaded'

  // Multi-day export
  const [showExportPanel, setShowExportPanel] = useState(false)
  const [exportStart, setExportStart] = useState('')
  const [exportEnd, setExportEnd] = useState('')
  const [exportInProgress, setExportInProgress] = useState(false)

  // Multi-day delete
  const [showDeletePanel, setShowDeletePanel] = useState(false)
  const [deleteStart, setDeleteStart] = useState('')
  const [deleteEnd, setDeleteEnd] = useState('')
  const [deleteDays, setDeleteDays] = useState([])
  const [deleteInProgress, setDeleteInProgress] = useState(false)

  // Kitchen-only daily steak-service mode selector. Three values:
  //   'vors'        — default, full plate is 2 steak + 1 vors
  //   'no_vors'     — vors not served, full plate is 2.5 steak only
  //   'extra_steak' — vors not served AND plate rebalanced to 3 steak
  // Persisted per dept+date in localStorage and as a meta doc on Firestore.
  const [steakMode, setSteakModeState] = useState('vors')

  const isBakeryType = currentDepartmentId === 'bakery' || currentDepartmentId === 'yawehs'
  const isTuckshop = currentDepartmentId === 'tuckshop'
  const isKitchen = !isBakeryType && !isTuckshop
  const dataTypes = getDataTypes(currentDepartmentId)
  const defaultType = isTuckshop ? 'sales' : isBakeryType ? 'production' : 'morning'

  const [dataType, setDataType] = useState(searchParams.get('type') || defaultType)

  useEffect(() => {
    const t = searchParams.get('type')
    if (t && dataTypes.some(d => d.id === t)) setDataType(t)
  }, [searchParams])

  useEffect(() => {
    if (!dataTypes.some(d => d.id === dataType)) {
      setDataType(dataTypes[0].id)
    }
  }, [currentDepartmentId])

  // Load steak-service mode for the current kitchen day.
  // Sync localStorage read first (instant), then async Firestore fallback.
  // Default to 'vors' if neither has a value. Backward-compat for legacy
  // boolean storage values is handled inside getVorsWithSteak.
  useEffect(() => {
    if (!isKitchen) return
    const local = getVorsWithSteak(currentDepartmentId, currentDate)
    if (local !== null) {
      setSteakModeState(local)
      return
    }
    setSteakModeState('vors')
    let cancelled = false
    fetchVorsWithSteak(currentDepartmentId, currentDate).then(v => {
      if (!cancelled && v !== null) setSteakModeState(v)
    })
    return () => { cancelled = true }
  }, [currentDepartmentId, currentDate, isKitchen])

  // Mode selector handler — updates local state and persists immediately.
  const handleSteakModeChange = useCallback((next) => {
    setSteakModeState(next)
    setVorsWithSteak(currentDepartmentId, currentDate, next)
  }, [currentDepartmentId, currentDate])

  // Check completion for current date
  const isCurrentDayComplete = useMemo(
    () => dayHasData(currentDepartmentId, currentDate, dataType),
    [currentDepartmentId, currentDate, dataType, completionKey]
  )

  // Compute which dates in the visible calendar month have any saved entries
  const datesWithEntries = useMemo(() => {
    const year = calendarMonth.getFullYear()
    const month = calendarMonth.getMonth()
    // All days in this month
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const result = []
    const allTypes = getDataTypes(currentDepartmentId).map(dt => dt.id)
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      const hasAny = allTypes.some(t => dayHasData(currentDepartmentId, iso, t))
      if (hasAny) result.push(new Date(year, month, d))
    }
    return result
  }, [currentDepartmentId, calendarMonth, completionKey])

  // Convert ISO string to Date object for the calendar and back
  const selectedDateObj = useMemo(() => {
    if (!currentDate) return undefined
    const [y, m, d] = currentDate.split('-').map(Number)
    return new Date(y, m - 1, d)
  }, [currentDate])

  const handleCalendarSelect = useCallback((date) => {
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    setCurrentDate(iso)
  }, [])

  // Day navigation
  const goToPrevDay = () => {
    const d = new Date(currentDate)
    d.setDate(d.getDate() - 1)
    setCurrentDate(d.toISOString().split('T')[0])
  }

  const goToNextDay = () => {
    const d = new Date(currentDate)
    d.setDate(d.getDate() + 1)
    setCurrentDate(d.toISOString().split('T')[0])
  }

  // Multi-day range generation
  const generateRange = () => {
    if (!rangeStart || !rangeEnd) {
      addToast('Select both start and end dates', 'error')
      return
    }
    if (rangeEnd < rangeStart) {
      addToast('End date must be after start date', 'error')
      return
    }
    const days = getDatesBetween(rangeStart, rangeEnd)
    setSelectedDays(days)
    setRangeConfirmed(false)
  }

  const toggleDay = (date) => {
    setSelectedDays(prev =>
      prev.includes(date)
        ? prev.filter(d => d !== date)
        : [...prev, date].sort()
    )
  }

  const confirmRange = () => {
    if (selectedDays.length === 0) {
      addToast('Select at least one day', 'error')
      return
    }
    setRangeConfirmed(true)
  }

  // Notify child components when save happens so we re-check completion
  const onEntrySaved = useCallback(() => {
    setCompletionKey(k => k + 1)
  }, [])

  // Multi-day delete logic
  const generateDeleteRange = () => {
    if (!deleteStart || !deleteEnd) { addToast('Select both start and end dates', 'error'); return }
    if (deleteEnd < deleteStart) { addToast('End date must be after start date', 'error'); return }
    setDeleteDays(getDatesBetween(deleteStart, deleteEnd))
  }

  const toggleDeleteDay = (date) => {
    setDeleteDays(prev => prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date].sort())
  }

  const handleMultiDelete = async () => {
    const daysWithData = deleteDays.filter(d => dayHasData(currentDepartmentId, d, dataType))
    if (daysWithData.length === 0) {
      addToast('No data to delete for the selected days', 'warning')
      return
    }
    if (!confirm(`Delete ${dataType} data for ${daysWithData.length} day(s) in ${currentDepartmentId}? This cannot be undone.`)) return

    setDeleteInProgress(true)
    let totalDeleted = 0

    for (const date of daysWithData) {
      if (isBakeryType) {
        if (dataType === 'production') totalDeleted += deleteProductionForDate(date, currentDepartmentId)
        else if (dataType === 'distribution') totalDeleted += deleteDistributionForDate(date, currentDepartmentId)
      } else {
        if (dataType === 'morning') totalDeleted += deleteMorningLogBatch(currentDepartmentId, date)
        else if (dataType === 'surplus') totalDeleted += deleteSurplusBatch(currentDepartmentId, date)
        else if (dataType === 'sales') totalDeleted += await deleteSalesForDate(currentDepartmentId, date)
      }
    }

    setDeleteInProgress(false)
    setDeleteDays([])
    setDeleteStart('')
    setDeleteEnd('')
    setCompletionKey(k => k + 1)
    addToast(`Deleted ${totalDeleted} entries across ${daysWithData.length} day(s)`, 'success')
  }

  // Check completion for a set of recent days (for the day bar)
  const recentDays = useMemo(() => {
    const days = []
    for (let i = -3; i <= 3; i++) {
      const d = new Date(currentDate)
      d.setDate(d.getDate() + i)
      const iso = d.toISOString().split('T')[0]
      days.push({
        date: iso,
        label: dayLabel(iso),
        hasData: dayHasData(currentDepartmentId, iso, dataType),
        isCurrent: iso === currentDate,
        isToday: iso === today,
      })
    }
    return days
  }, [currentDate, currentDepartmentId, dataType, completionKey])

  const renderContent = () => {
    if (mode === 'manual') {
      if (isBakeryType && !isTuckshop) {
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <EntrySection label="Production Log" dataType="production" date={currentDate} onSaved={onEntrySaved} isBakery />
              <EntrySection label="Distribution" dataType="distribution" date={currentDate} onSaved={onEntrySaved} isBakery />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <EntrySection label="Daily Collections" dataType="daily-collections" date={currentDate} onSaved={onEntrySaved} isBakery />
              <EntrySection label="Damages Log" dataType="damages" date={currentDate} onSaved={onEntrySaved} isBakery />
            </div>
            <details className="bg-gray-900 border border-gray-800 rounded-lg">
              <summary className="px-4 py-3 text-sm text-gray-400 cursor-pointer hover:text-white">
                Shop Sales (direct counter sales)
              </summary>
              <div className="p-4 border-t border-gray-800">
                <EntrySection label="Shop Sales" dataType="shop-sales" date={currentDate} onSaved={onEntrySaved} isBakery />
              </div>
            </details>
          </div>
        )
      }
      // Kitchen: show all three sections side by side, each with its own mode selector
      if (!isTuckshop) {
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <EntrySection label="Morning Log" dataType="morning" date={currentDate} onSaved={onEntrySaved} steakMode={steakMode} />
            <EntrySection label="Sales" dataType="sales" date={currentDate} onSaved={onEntrySaved} />
            <EntrySection label="Surplus" dataType="surplus" date={currentDate} onSaved={onEntrySaved} />
          </div>
        )
      }
      return <ManualEntry dataType={dataType} date={currentDate} onSaved={onEntrySaved} steakMode={steakMode} />
    }
    if (mode === 'monthly-summary') {
      return <BakeryEntry dataType="monthly-summary" date={currentDate} />
    }
    if (mode === 'credit-tracker') {
      return <BakeryEntry dataType="credit-tracker" date={currentDate} />
    }
    if (mode === 'add-to-log') {
      return <AddToLog onSaved={onEntrySaved} />
    }
    if (mode === 'single-upload') {
      return <SingleDayUpload dataType={dataType} date={currentDate} onSaved={onEntrySaved} />
    }
    if (mode === 'bulk') {
      if (!rangeConfirmed && selectedDays.length > 0) return null // show range UI instead
      const props = { dataType, onSaved: onEntrySaved }
      if (rangeConfirmed) props.dateRange = selectedDays
      return bulkFormat === 'pdf'
        ? <PdfUpload {...props} />
        : <ExcelUpload {...props} />
    }
    return null
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold">Data Entry & Ingestion</h1>

      {/* Compact secondary mode bar — only visible when not in the default manual entry view */}
      <div className="flex items-center gap-2 flex-wrap">
        {MODES.filter(m => m.id !== 'manual').map(m => {
          const Icon = m.icon
          return (
            <button
              key={m.id}
              onClick={() => { setMode(mode === m.id ? 'manual' : m.id); setRangeConfirmed(false); setSelectedDays([]) }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border transition-all ${
                mode === m.id
                  ? 'border-brand-500 bg-brand-950/30 text-brand-400'
                  : 'border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-500'
              }`}
            >
              <Icon size={13} />
              {m.label}
            </button>
          )
        })}

        {/* Bakery-only report tabs */}
        {isBakeryType && !isTuckshop && (
          <>
            <span className="text-gray-700 mx-1">|</span>
            {[
              { id: 'monthly-summary', label: 'Monthly Summary' },
              { id: 'credit-tracker', label: 'Credit Tracker' },
            ].map(m => (
              <button
                key={m.id}
                onClick={() => setMode(mode === m.id ? 'manual' : m.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border transition-all ${
                  mode === m.id
                    ? 'border-amber-500 bg-amber-950/30 text-amber-400'
                    : 'border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-500'
                }`}
              >
                {m.label}
              </button>
            ))}
          </>
        )}

        {/* Data type selector for non-manual modes (tuckshop, bakery upload modes) */}
        {mode !== 'manual' && mode !== 'add-to-log' && (
          <div className="flex gap-1.5 ml-3 pl-3 border-l border-gray-700">
            {dataTypes.map(dt => (
              <button
                key={dt.id}
                onClick={() => setDataType(dt.id)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  dataType === dt.id
                    ? 'bg-brand-700 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                {dt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Inline calendar date picker — always visible for manual + single-upload modes */}
      {(mode === 'manual' || mode === 'single-upload') && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
          {/* Header row: selected date label + export button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarDays size={15} className="text-brand-400" />
              <span className="text-sm font-medium text-gray-200">
                {selectedDateObj
                  ? selectedDateObj.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                  : 'Select a date'}
              </span>
              {isCurrentDayComplete && (
                <span className="flex items-center gap-1 text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">
                  <CheckCircle size={11} /> Saved
                </span>
              )}
            </div>
            <button
              onClick={async () => {
                const ok = await exportDayToExcel(currentDepartmentId, currentDate)
                if (ok) addToast(`Exported ${currentDate} data`, 'success')
                else addToast('No data to export for this date', 'warning')
              }}
              className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-gray-300 px-2.5 py-1 rounded text-xs"
              title="Export day's data as Excel"
            >
              <Download size={12} /> Export
            </button>
          </div>

          {/* Full inline calendar */}
          <DateCalendar
            selectedDate={selectedDateObj}
            onDateSelect={handleCalendarSelect}
            datesWithEntries={datesWithEntries}
            maxDate={new Date()}
            onMonthChange={(month) => setCalendarMonth(new Date(month.getFullYear(), month.getMonth(), 1))}
          />

          {/* Fallback text date input for manual override */}
          <div className="flex items-center gap-2 pt-1 border-t border-gray-800">
            <span className="text-xs text-gray-500">Or type date:</span>
            <DateInput value={currentDate} onChange={setCurrentDate} />
          </div>
        </div>
      )}

      {/* Steak service mode selector — kitchen only. Three pill buttons
          act as a segmented control: With Vors / No Vors / Extra Steak.
          Sub-text below the buttons updates based on selection. */}
      {(mode === 'manual' || mode === 'single-upload') && isKitchen && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
          <p className="text-sm font-medium text-gray-200">Steak configuration for today:</p>
          <div
            role="radiogroup"
            aria-label="Steak configuration for today"
            className="flex items-center gap-2 flex-wrap"
          >
            {[
              { value: 'vors',        label: '✓ Served with Vors', color: 'green' },
              { value: 'no_vors',     label: 'No Vors',             color: 'red' },
              { value: 'extra_steak', label: 'Extra Steak',         color: 'amber' },
            ].map(opt => {
              const isActive = steakMode === opt.value
              const base = 'px-4 py-2 rounded-full text-xs font-semibold border transition-colors'
              let cls
              if (isActive) {
                if (opt.color === 'green')      cls = `${base} bg-green-600 border-green-500 text-white`
                else if (opt.color === 'red')   cls = `${base} bg-red-600 border-red-500 text-white`
                else /* amber */                cls = `${base} bg-amber-600 border-amber-500 text-white`
              } else {
                if (opt.color === 'green')      cls = `${base} bg-transparent border-green-700/50 text-green-300 hover:bg-green-950/30`
                else if (opt.color === 'red')   cls = `${base} bg-transparent border-red-700/50 text-red-300 hover:bg-red-950/30`
                else /* amber */                cls = `${base} bg-transparent border-amber-700/50 text-amber-300 hover:bg-amber-950/30`
              }
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => handleSteakModeChange(opt.value)}
                  className={cls}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
          <p className="text-[11px] text-gray-500">
            {steakMode === 'vors'        && 'Full plate: 2 steak + 1 vors  |  Half plate: 1 steak + 1 vors'}
            {steakMode === 'no_vors'     && 'Full plate: 2.5 steak only  |  Half plate: 1.5 steak only'}
            {steakMode === 'extra_steak' && 'Full plate: 3 steak only  |  Half plate: 2 steak only'}
          </p>
        </div>
      )}

      {/* Bulk format selector */}
      {mode === 'bulk' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">Upload format:</span>
            <button
              onClick={() => setBulkFormat('excel')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                bulkFormat === 'excel'
                  ? 'bg-brand-700 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <FileSpreadsheet size={14} /> Excel (.xlsx)
            </button>
            <button
              onClick={() => setBulkFormat('pdf')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                bulkFormat === 'pdf'
                  ? 'bg-brand-700 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <Upload size={14} /> PDF
            </button>
          </div>

          {/* Date range selector */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 space-y-3">
            <p className="text-sm font-medium text-white flex items-center gap-2">
              <CalendarDays size={16} className="text-brand-400" />
              Select Date Range
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Start Date</label>
                <DateInput value={rangeStart} onChange={setRangeStart} />
              </div>
              <span className="text-gray-600 mt-5">to</span>
              <div>
                <label className="text-xs text-gray-400 block mb-1">End Date</label>
                <DateInput value={rangeEnd} onChange={setRangeEnd} />
              </div>
              <button
                onClick={generateRange}
                className="mt-5 bg-brand-700 hover:bg-brand-600 text-white text-xs px-4 py-2 rounded font-medium"
              >
                Generate Days
              </button>
            </div>

            {/* Day selector - deselectable */}
            {selectedDays.length > 0 && !rangeConfirmed && (
              <div className="space-y-2">
                <p className="text-xs text-gray-400">{selectedDays.length} days selected — click to deselect individual days:</p>
                <div className="flex gap-1.5 flex-wrap">
                  {getDatesBetween(rangeStart, rangeEnd).map(date => {
                    const isSelected = selectedDays.includes(date)
                    const hasData = dayHasData(currentDepartmentId, date, dataType)
                    return (
                      <button
                        key={date}
                        onClick={() => toggleDay(date)}
                        className={`relative px-3 py-1.5 rounded text-xs font-medium transition-all ${
                          isSelected
                            ? 'bg-brand-700 text-white'
                            : 'bg-gray-800 text-gray-500 line-through'
                        }`}
                      >
                        {dayLabel(date)}
                        {hasData && (
                          <CheckCircle size={10} className="absolute -top-1 -right-1 text-green-400 fill-green-400/20" />
                        )}
                      </button>
                    )
                  })}
                </div>
                <button
                  onClick={confirmRange}
                  className="bg-green-700 hover:bg-green-600 text-white text-xs px-4 py-2 rounded font-medium"
                >
                  Confirm Range ({selectedDays.length} days) & Continue
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Single-day upload format selector */}
      {mode === 'single-upload' && (
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">Accepted: Excel (.xlsx, .xls) and PDF</span>
        </div>
      )}

      {/* Main content area */}
      <div className={mode === 'manual' && !isTuckshop ? '' : 'bg-gray-900 border border-gray-800 rounded-lg p-6'}>
        {renderContent()}
      </div>


      {/* Multi-day export */}
      <div className="border-t border-gray-800 pt-4">
        <button
          onClick={() => setShowExportPanel(p => !p)}
          className="flex items-center gap-2 text-xs text-gray-500 hover:text-green-400 transition-colors"
        >
          <Download size={14} />
          {showExportPanel ? 'Hide Export Panel' : 'Export Data for Date Range'}
        </button>

        {showExportPanel && (
          <div className="mt-3 bg-green-950/20 border border-green-900/50 rounded-lg p-4 space-y-3">
            <p className="text-sm font-medium text-green-400 flex items-center gap-2">
              <Download size={14} /> Export to Excel — All Data Types
            </p>
            <p className="text-xs text-gray-400">
              Export all data for <span className="text-white font-medium">{currentDepartmentId}</span> between the selected dates. Days with no data will be included and marked.
            </p>

            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Start Date</label>
                <DateInput value={exportStart} onChange={setExportStart} />
              </div>
              <span className="text-gray-600 mt-5">to</span>
              <div>
                <label className="text-xs text-gray-400 block mb-1">End Date</label>
                <DateInput value={exportEnd} onChange={setExportEnd} />
              </div>
              <button
                onClick={async () => {
                  if (!exportStart || !exportEnd) { addToast('Select both dates', 'error'); return }
                  if (exportEnd < exportStart) { addToast('End date must be after start', 'error'); return }
                  setExportInProgress(true)
                  try {
                    await exportRangeToExcel(currentDepartmentId, exportStart, exportEnd)
                    addToast(`Exported ${currentDepartmentId} data from ${exportStart} to ${exportEnd}`, 'success')
                  } catch (err) {
                    addToast('Export failed: ' + err.message, 'error')
                  }
                  setExportInProgress(false)
                }}
                disabled={exportInProgress}
                className="mt-5 flex items-center gap-2 bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white text-xs px-4 py-2 rounded font-medium"
              >
                <Download size={12} />
                {exportInProgress ? 'Exporting...' : 'Export Excel'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Multi-day delete */}
      <div className="border-t border-gray-800 pt-4">
        <button
          onClick={() => setShowDeletePanel(p => !p)}
          className="flex items-center gap-2 text-xs text-gray-500 hover:text-red-400 transition-colors"
        >
          <Trash2 size={14} />
          {showDeletePanel ? 'Hide Delete Panel' : 'Delete Data for Multiple Days'}
        </button>

        {showDeletePanel && (
          <div className="mt-3 bg-red-950/20 border border-red-900/50 rounded-lg p-4 space-y-3">
            <p className="text-sm font-medium text-red-400 flex items-center gap-2">
              <Trash2 size={14} /> Multi-Day Delete — {dataTypes.find(d => d.id === dataType)?.label}
            </p>
            <p className="text-xs text-gray-400">
              Select a date range to delete <span className="text-white font-medium">{dataTypes.find(d => d.id === dataType)?.label}</span> data for the <span className="text-white font-medium">{currentDepartmentId}</span> department.
            </p>

            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Start Date</label>
                <DateInput value={deleteStart} onChange={setDeleteStart} />
              </div>
              <span className="text-gray-600 mt-5">to</span>
              <div>
                <label className="text-xs text-gray-400 block mb-1">End Date</label>
                <DateInput value={deleteEnd} onChange={setDeleteEnd} />
              </div>
              <button
                onClick={generateDeleteRange}
                className="mt-5 bg-gray-800 hover:bg-gray-700 text-white text-xs px-4 py-2 rounded font-medium"
              >
                Show Days
              </button>
            </div>

            {deleteDays.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-400">Click to deselect days. Days with data are highlighted:</p>
                <div className="flex gap-1.5 flex-wrap">
                  {getDatesBetween(deleteStart, deleteEnd).map(date => {
                    const isSelected = deleteDays.includes(date)
                    const hasData = dayHasData(currentDepartmentId, date, dataType)
                    return (
                      <button
                        key={date}
                        onClick={() => toggleDeleteDay(date)}
                        className={`relative px-3 py-1.5 rounded text-xs font-medium transition-all ${
                          !isSelected
                            ? 'bg-gray-800 text-gray-500 line-through'
                            : hasData
                              ? 'bg-red-900/60 text-red-300 border border-red-700'
                              : 'bg-gray-800 text-gray-400'
                        }`}
                      >
                        {dayLabel(date)}
                        {hasData && isSelected && (
                          <CheckCircle size={10} className="absolute -top-1 -right-1 text-red-400 fill-red-400/20" />
                        )}
                      </button>
                    )
                  })}
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={handleMultiDelete}
                    disabled={deleteInProgress}
                    className="flex items-center gap-2 bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white text-xs px-4 py-2 rounded font-medium"
                  >
                    <Trash2 size={12} />
                    {deleteInProgress ? 'Deleting...' : `Delete ${deleteDays.filter(d => dayHasData(currentDepartmentId, d, dataType)).length} day(s) with data`}
                  </button>
                  <button
                    onClick={() => { setDeleteDays([]); setDeleteStart(''); setDeleteEnd('') }}
                    className="text-xs text-gray-500 hover:text-white"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Daily Sales & Analysis embedded below data entry */}
      <div className="border-t border-gray-800 pt-6 mt-6">
        <DailyOps date={currentDate} />
      </div>
    </div>
  )
}
